import groupBy from 'lodash.groupby';
import partition from 'lodash.partition';
import { toDataFieldWithDataType } from '../../../../common-types';
import {
  singleValueTimeSegment,
  timeSliderGranularityToTimeSelectionInput,
} from '../../../pages/dashboard/balance-sheet/backend-metrics/utils';
import {
  PbsSegmentationServiceInputs,
  PbsSegmentationServiceResponse,
  PbsServiceResponseData,
  PbsServiceResponseDataMonoid,
  PERIOD_TOTAL,
  SegmentedResponseData,
  SegmentedResponseDataMetaData,
  ValueByDate,
} from '../../../pages/dashboard/balance-sheet/types';
import {
  formatPeriodConfigDate,
  getFiltersAndSegmentsForMetricKind,
  getGranularityConfig,
  getHeaderObjs,
  getPbsServiceResponseData,
  getTimeSlicesAccToTimeSliderConfig,
  SegmentationQueryInputs,
} from '../../../pages/dashboard/balance-sheet/utils';
import {
  $SEGMENT_SEPARATOR$,
  ApiMasterDataQueryResponseDataPoint,
  ApiMetricQueryResponse,
  ApiMetricQueryResponseDataPoint,
} from '../../api/api-interfaces';
import { CachedMasterDataService } from '../../api/cached-master-data-service';
import { SQLFilters } from '../../api/master-data-service';
import { QueryToSqlHelper } from '../../api/query-to-sql-helper';
import {
  SegmentOthersString,
  UndefinedValue,
} from '../../constants/systemValuesTranslation';
import {
  CohortMetricId,
  EmployeeCohortMetricId,
  GroupingInput,
  MetricGroupId,
  MetricId,
  RegularMetricId,
  TimeSelectionInput,
} from '../../graphql/generated/graphql-sdk';
import { getModifiedInputsAndFiltersForBenchmark } from '../../services/BenchmarkWrapperService';
import { getReactQueryWrappedService } from '../../services/utils';
import { allValuesEqual, getBaseField, uuid } from '../../utilFunctions/utils';
import { MetricDetails } from '../../v2/api/metrics/types';
import { VersionId } from '../../v2/api/types';
import { toGroupingInput } from '../../v2/api/utils';
import { Filter } from '../../v2/common/components/filter/filterbar/types';
import { toSegmentation } from '../../v2/common/components/filter/utils';
import { MetricTypes } from '../../v2/common/types';
import { $HierarchicalFilterSeparator$ } from '../../v2/constants';
import {
  LegacyDomainScopedStore,
  LegacyStore,
} from '../../v2/context/legacy-types';
import { DataTypes } from '../../v2/types';
import { MetricStore } from '../metric-store';
import { getNegatedSegmentationFilters } from './getNegatedSegmentationFilters';

const getRegularSegmentValue = (dp: ApiMasterDataQueryResponseDataPoint) => {
  return Object.values(
    groupBy(dp.dimensions, (d) =>
      JSON.stringify(
        getBaseField(toDataFieldWithDataType(d.dataType, d.property))
      )
    )
  )
    .map((dims) =>
      dims
        .map((d) => (d.value as string) ?? UndefinedValue)
        .join($HierarchicalFilterSeparator$)
    )
    .filter(Boolean)
    .join($SEGMENT_SEPARATOR$);
};

const getCohortSegmentValue = (dp: ApiMasterDataQueryResponseDataPoint) => {
  // How would hierarchical response look like for cohort segmentation
  // Here I am assuming only level 1 is present
  return dp.dimensions
    .map((d) => {
      //  Since this looks like
      //  {
      //    dataType: "COHORT",
      //    dataField: "COHORT_VALUE",
      //    value: {CUSTOM_FIELD_LEVEL_1: XYZ}
      //  }
      return Object.values(d.value)[0];
    })
    .join($SEGMENT_SEPARATOR$);
};

interface SegmentationBackend {
  metricType: MetricTypes;
  isMetricOfType: (metricStore: MetricStore, metric: MetricDetails) => boolean;
  queryMetrics: (
    timeSelection: TimeSelectionInput,
    metrics: MetricId[],
    userFilters?: SQLFilters | null,
    grouping?: GroupingInput,
    disableNestLoop?: boolean
  ) => Promise<ApiMetricQueryResponse>;
  getSegmentValue: (dp: ApiMasterDataQueryResponseDataPoint) => string | null;
}

const getSegmentationBackends = (
  cachedMasterDataApiService: CachedMasterDataService
): SegmentationBackend[] => [
  {
    metricType: MetricTypes.REGULAR,
    isMetricOfType: (metricStore, metric) =>
      metricStore.isMetricRegular(metric.id),
    queryMetrics: (
      timeSelection,
      metricIds,
      userFilters,
      grouping,
      disableNestLoop
    ) =>
      cachedMasterDataApiService.queryMetrics(
        timeSelection,
        metricIds as unknown as RegularMetricId[],
        userFilters,
        grouping,
        disableNestLoop
      ),
    getSegmentValue: getRegularSegmentValue,
  },
  {
    metricType: MetricTypes.COHORT,
    isMetricOfType: (metricStore, metric) =>
      metricStore.isMetricCohort(metric.id),
    queryMetrics: (
      timeSelection,
      metricIds,
      userFilters,
      grouping,
      disableNestLoop
    ) =>
      cachedMasterDataApiService.queryCohortMetrics(
        timeSelection,
        metricIds as unknown as CohortMetricId[],
        userFilters,
        grouping,
        disableNestLoop
      ),
    getSegmentValue: getCohortSegmentValue,
  },
  {
    metricType: MetricTypes.EMPLOYEE_COHORT,
    isMetricOfType: (metricStore, metric) =>
      metricStore.isMetricEmployeeCohort(metric.id),
    queryMetrics: (
      timeSelection,
      metricIds,
      userFilters,
      grouping,
      disableNestLoop
    ) =>
      cachedMasterDataApiService.queryEmployeeCohortMetrics(
        timeSelection,
        metricIds as unknown as EmployeeCohortMetricId[],
        userFilters,
        undefined,
        grouping,
        disableNestLoop
      ),
    getSegmentValue: getCohortSegmentValue,
  },
];

interface SegmentedResult {
  responseData: SegmentedResponseData;
  meta: SegmentedResponseDataMetaData;
}

const mergeSegmentedResults = (
  metricGroupId: MetricGroupId,
  results: SegmentedResult[]
): SegmentedResult => {
  const responseData: SegmentedResponseData = { [metricGroupId]: {} };
  const meta: SegmentedResponseDataMetaData = { [metricGroupId]: {} };
  results.forEach((result) => {
    Object.entries(result.responseData[metricGroupId] ?? {}).forEach(
      ([segment, valueByDate]) => {
        const target = (responseData[metricGroupId][segment] =
          responseData[metricGroupId][segment] ?? {});
        Object.entries(valueByDate).forEach(([dateKey, valueByMetric]) => {
          target[dateKey] = { ...target[dateKey], ...valueByMetric };
        });
      }
    );
    Object.entries(result.meta[metricGroupId] ?? {}).forEach(
      ([segment, segmentMeta]) => {
        const existing = meta[metricGroupId][segment];
        meta[metricGroupId][segment] = existing
          ? { ...existing, meta: { ...existing.meta, ...segmentMeta.meta } }
          : segmentMeta;
      }
    );
  });
  return { responseData, meta };
};

// A metric group can mix metric types (e.g. a FINANCIALMETRICS-table metric next
// to its cohort variant), so each metric is queried through the backend for its
// own type, with filters/segments narrowed for that type, and the results are
// merged (same idea as ENG-8825 in PbsTableService).
export const getPbsSegmentationServiceForMetricGroup = (
  metricId: MetricGroupId,
  cachedMasterDataApiService: CachedMasterDataService
) =>
  getPbsSegmentationServiceForBackendMetrics(
    metricId,
    getSegmentationBackends(cachedMasterDataApiService)
  );

const getPbsSegmentationServiceForBackendMetrics = <
  MetricGroupIdInstance extends MetricGroupId,
>(
  metricId: MetricGroupIdInstance,
  backends: SegmentationBackend[]
) => {
  const PbsSegmentationServiceForBackendMetrics = async (
    inputs: PbsSegmentationServiceInputs,
    filters: Filter[],
    latestVersions: Partial<Record<DataTypes, VersionId>>,
    legacyStores: LegacyStore,
    legacyDomainScopedStores: LegacyDomainScopedStore
  ) => {
    const { metricStore, timeSliderStore } = legacyDomainScopedStores;
    const {
      timeSliderConfig,
      segmentationLevel1,
      segmentationLevel2,
      selectedBenchmark,
    } = inputs;

    // Add validation for duplicate dimensions
    if (segmentationLevel2.length > 0) {
      const isDuplicateDimension = segmentationLevel1.some((s1) =>
        segmentationLevel2.some(
          (s2) => s1.dataType === s2.dataType && s1.property === s2.property
        )
      );
      if (isDuplicateDimension) {
        throw new Error('Segmentation levels cannot use the same dimension');
      }
    }

    const selectedmetricDetails =
      metricStore.getAllowedSortedMetricIdsForMetricGroup(metricId);

    if (selectedmetricDetails.length <= 0) {
      throw new Error('At least one metric must be selected');
    }
    if (segmentationLevel1.length === 0) {
      throw new Error('Segmentation must be selected');
    }

    const runSegmentationQueries = async (
      { metricType, queryMetrics, getSegmentValue }: SegmentationBackend,
      selectedmetricDetails: MetricDetails[],
      {
        filters,
        segmentationLevel1,
        segmentationLevel2,
      }: SegmentationQueryInputs
    ) => {
      const sqlUserFilters = QueryToSqlHelper.getFilterConditions(
        filters,
        metricType,
        false
      );
      const sqlUserAndOthersFilters =
        QueryToSqlHelper.getFilterConditions(
          [...filters, ...getNegatedSegmentationFilters(segmentationLevel1)],
          metricType,
          false
        ) ?? undefined;

      const segmentationsLevel1 = toSegmentation(
        segmentationLevel1,
        metricType
      );
      const groupingsSegmentLevel1: GroupingInput | undefined =
        segmentationsLevel1 ? toGroupingInput(segmentationsLevel1) : undefined;
      let backendBenchmarkPromiseSegmentLevel1:
        | (() => Promise<{ dataPoints: ApiMetricQueryResponseDataPoint[] }>)
        | null = null;
      let backendBenchmarkPromiseOthersSegmentLevel1:
        | (() => Promise<{ dataPoints: ApiMetricQueryResponseDataPoint[] }>)
        | null = null;
      if (selectedBenchmark) {
        const { inputs: benchmarkInputs, filters: benchmarkFilters } =
          getModifiedInputsAndFiltersForBenchmark(
            inputs,
            filters,
            [],
            timeSliderConfig,
            selectedBenchmark
          );
        const benchmarkTimeSliderConfig = benchmarkInputs.timeSliderConfig;
        const benchmarkSqlUserFilters = QueryToSqlHelper.getFilterConditions(
          benchmarkFilters as Filter[],
          metricType,
          false
        );
        backendBenchmarkPromiseSegmentLevel1 = () =>
          queryMetrics(
            singleValueTimeSegment(benchmarkTimeSliderConfig),
            selectedmetricDetails.map((m) => m.id),
            benchmarkSqlUserFilters,
            groupingsSegmentLevel1
          );
        backendBenchmarkPromiseOthersSegmentLevel1 = () =>
          queryMetrics(
            singleValueTimeSegment(benchmarkTimeSliderConfig),
            selectedmetricDetails.map((m) => m.id),
            // Shaq TODO: Shouldn't this be benchmarkSqlUserFilters as well
            sqlUserAndOthersFilters
          );
      }

      const backendOverTimePromiseSegmentLevel1 = () =>
        queryMetrics(
          timeSliderGranularityToTimeSelectionInput(timeSliderConfig),
          selectedmetricDetails.map((m) => m.id),
          sqlUserFilters,
          groupingsSegmentLevel1
        );
      const backendTotalPeriodPromiseSegmentLevel1 = () =>
        queryMetrics(
          singleValueTimeSegment(timeSliderConfig),
          selectedmetricDetails.map((m) => m.id),
          sqlUserFilters,
          groupingsSegmentLevel1
        );

      const backendOverTimePromiseOthersSegmentLevel1 = () =>
        queryMetrics(
          timeSliderGranularityToTimeSelectionInput(timeSliderConfig),
          selectedmetricDetails.map((m) => m.id),
          sqlUserAndOthersFilters
        );
      const backendTotalPeriodPromiseOthersSegmentLevel1 = () =>
        queryMetrics(
          singleValueTimeSegment(timeSliderConfig),
          selectedmetricDetails.map((m) => m.id),
          sqlUserAndOthersFilters
        );

      const executeSqlQuery: (
        selectedmetricDetails: MetricDetails[],
        overTimePromise: () => Promise<ApiMetricQueryResponse>,
        totalPeriodPromise: () => Promise<ApiMetricQueryResponse>,
        benchmarkPromise: (() => Promise<ApiMetricQueryResponse>) | null,
        othersPromises: boolean
      ) => Promise<{
        responseData: SegmentedResponseData;
        meta: SegmentedResponseDataMetaData;
      }> = async (
        selectedmetricDetails,
        overTimePromise,
        totalPeriodPromise,
        benchmarkPromise,
        othersPromises
      ) => {
        if (selectedmetricDetails.length === 0) {
          return Promise.resolve({ responseData: {}, meta: {} });
        }
        return Promise.all([
          overTimePromise(),
          totalPeriodPromise(),
          ...(benchmarkPromise ? [benchmarkPromise()] : []),
        ]).then(([overTimeResult, periodTotalResult, benchmarkResult]) => {
          const meta: SegmentedResponseDataMetaData = {};
          const responseData: SegmentedResponseData = {};
          selectedmetricDetails.map((metric) => {
            responseData[metric.metricGroupId] =
              responseData[metric.metricGroupId] || {};
            const getFinalSegmentValue = (
              dp: ApiMasterDataQueryResponseDataPoint
            ): string | null => {
              // Assumes that the backend returns dimensions in order. It would be a lot more painful to figure out the order dynamically
              const segmentValue = getSegmentValue(dp);
              if (othersPromises) {
                return `${SegmentOthersString}${segmentValue ? `${$SEGMENT_SEPARATOR$}${segmentValue}` : ''}`;
              } else if (dp.dimensions.length === 0) {
                // This means there's no segment. We are dealing with dummy segment response
                return null;
              } else {
                return segmentValue;
              }
            };
            const populateDataForDps = (
              dps: ApiMetricQueryResponseDataPoint[],
              getDataKey: (dp: ApiMetricQueryResponseDataPoint) => string,
              getSegmentV: (
                dp: ApiMetricQueryResponseDataPoint
              ) => string | null,
              metaDataKey: 'overtime' | 'periodTotal' | 'benchmark'
            ) => {
              dps.forEach((dp) => {
                const dataKey = getDataKey(dp);
                const segmentValue = getSegmentV(dp);
                if (!segmentValue) {
                  return;
                }
                responseData[metric.metricGroupId][segmentValue] =
                  responseData[metric.metricGroupId][segmentValue] || {};
                dp.measures.forEach((m) => {
                  if ((m.property as MetricId) === metric.id) {
                    responseData[metric.metricGroupId][segmentValue][dataKey] =
                      responseData[metric.metricGroupId][segmentValue][
                        dataKey
                      ] || {};
                    responseData[metric.metricGroupId][segmentValue][dataKey][
                      metric.id
                    ] = m?.value as number;
                  }
                });
                if (dp.meta) {
                  meta[metric.metricGroupId] = meta[metric.metricGroupId] ?? {};
                  meta[metric.metricGroupId][segmentValue] = meta[
                    metric.metricGroupId
                  ][segmentValue] ?? {
                    meta: {},
                  };
                  meta[metric.metricGroupId][segmentValue].meta[metric.id] =
                    meta[metric.metricGroupId][segmentValue].meta[metric.id] ??
                    {};
                  meta[metric.metricGroupId][segmentValue].meta[metric.id][
                    metaDataKey
                  ] = {
                    metricSql: dp.meta.metricSql,
                    metricGroupId: metric.metricGroupId,
                    metricId: metric.id,
                  };
                }
              });
            };
            populateDataForDps(
              overTimeResult.dataPoints ?? [],
              (dp) => dp.dimensions[0].value as string, // versionId
              (dp) =>
                getFinalSegmentValue({
                  ...dp,
                  dimensions: dp.dimensions.slice(1),
                }), // skip versionid
              'overtime'
            );
            populateDataForDps(
              periodTotalResult.dataPoints ?? [],
              () => PERIOD_TOTAL,
              (dp) => getFinalSegmentValue(dp),
              'periodTotal'
            );
            if (selectedBenchmark) {
              populateDataForDps(
                benchmarkResult.dataPoints ?? [],
                () => selectedBenchmark.value,
                (dp) => getFinalSegmentValue(dp),
                'benchmark'
              );
            }
          });
          return { responseData, meta };
        });
      };

      const executeBackendSqlQuerySegmentLevel1 = executeSqlQuery(
        selectedmetricDetails,
        backendOverTimePromiseSegmentLevel1,
        backendTotalPeriodPromiseSegmentLevel1,
        backendBenchmarkPromiseSegmentLevel1,
        false
      );
      const executeOthersBackendSqlQuerySegmentLevel1 = executeSqlQuery(
        selectedmetricDetails,
        backendOverTimePromiseOthersSegmentLevel1,
        backendTotalPeriodPromiseOthersSegmentLevel1,
        backendBenchmarkPromiseOthersSegmentLevel1,
        true
      );

      let executeBackendSqlQuerySegmentLevel2: Promise<{
        responseData: SegmentedResponseData;
        meta: SegmentedResponseDataMetaData;
      }> = Promise.resolve({
        responseData: { [metricId]: {} },
        meta: { [metricId]: {} },
      });

      let executeOthersBackendSqlQuerySegmentLevel2: Promise<{
        responseData: SegmentedResponseData;
        meta: SegmentedResponseDataMetaData;
      }> = Promise.resolve({
        responseData: { [metricId]: {} },
        meta: { [metricId]: {} },
      });

      if (segmentationLevel2.length) {
        const segmentationsLevel2 = toSegmentation(
          segmentationLevel2,
          metricType
        );

        const groupingsSegmentLevel2 = segmentationsLevel2
          ? toGroupingInput(segmentationsLevel2)
          : undefined;
        const groupingsSegmentLevel1SegmentLevel2 =
          segmentationsLevel1 && segmentationsLevel2
            ? toGroupingInput([
                ...(segmentationsLevel1 ?? []),
                ...(segmentationsLevel2 ?? []),
              ])
            : undefined;

        const backendOverTimePromiseSegmentLevel2 = () =>
          queryMetrics(
            timeSliderGranularityToTimeSelectionInput(timeSliderConfig),
            selectedmetricDetails.map((m) => m.id),
            sqlUserFilters,
            groupingsSegmentLevel1SegmentLevel2
          );
        const backendTotalPeriodPromiseSegmentLevel2 = () =>
          queryMetrics(
            singleValueTimeSegment(timeSliderConfig),
            selectedmetricDetails.map((m) => m.id),
            sqlUserFilters,
            groupingsSegmentLevel1SegmentLevel2
          );

        const backendOverTimePromiseOthersSegmentLevel2 = () =>
          queryMetrics(
            timeSliderGranularityToTimeSelectionInput(timeSliderConfig),
            selectedmetricDetails.map((m) => m.id),
            sqlUserAndOthersFilters,
            groupingsSegmentLevel2
          );
        const backendTotalPeriodPromiseOthersSegmentLevel2 = () =>
          queryMetrics(
            singleValueTimeSegment(timeSliderConfig),
            selectedmetricDetails.map((m) => m.id),
            sqlUserAndOthersFilters,
            groupingsSegmentLevel2
          );

        let backendBenchmarkPromiseSegmentLevel2:
          | (() => Promise<{ dataPoints: ApiMetricQueryResponseDataPoint[] }>)
          | null = null;
        let backendBenchmarkPromiseOthersSegmentLevel2:
          | (() => Promise<{ dataPoints: ApiMetricQueryResponseDataPoint[] }>)
          | null = null;
        if (selectedBenchmark) {
          const { inputs: benchmarkInputs, filters: benchmarkFilters } =
            getModifiedInputsAndFiltersForBenchmark(
              inputs,
              filters,
              [],
              timeSliderConfig,
              selectedBenchmark
            );
          const benchmarkTimeSliderConfig = benchmarkInputs.timeSliderConfig;
          const benchmarkSqlUserFilters = QueryToSqlHelper.getFilterConditions(
            benchmarkFilters as Filter[],
            metricType,
            false
          );
          backendBenchmarkPromiseSegmentLevel2 = () =>
            queryMetrics(
              singleValueTimeSegment(benchmarkTimeSliderConfig),
              selectedmetricDetails.map((m) => m.id),
              benchmarkSqlUserFilters,
              groupingsSegmentLevel1SegmentLevel2
            );
          backendBenchmarkPromiseOthersSegmentLevel2 = () =>
            queryMetrics(
              singleValueTimeSegment(benchmarkTimeSliderConfig),
              selectedmetricDetails.map((m) => m.id),
              sqlUserAndOthersFilters,
              groupingsSegmentLevel2
            );
        }

        executeBackendSqlQuerySegmentLevel2 = executeSqlQuery(
          selectedmetricDetails,
          backendOverTimePromiseSegmentLevel2,
          backendTotalPeriodPromiseSegmentLevel2,
          backendBenchmarkPromiseSegmentLevel2,
          false
        );
        executeOthersBackendSqlQuerySegmentLevel2 = executeSqlQuery(
          selectedmetricDetails,
          backendOverTimePromiseOthersSegmentLevel2,
          backendTotalPeriodPromiseOthersSegmentLevel2,
          backendBenchmarkPromiseOthersSegmentLevel2,
          true
        );
      }

      return {
        res1: await executeBackendSqlQuerySegmentLevel1,
        resOthers1: await executeOthersBackendSqlQuerySegmentLevel1,
        res2: await executeBackendSqlQuerySegmentLevel2,
        resOthers2: await executeOthersBackendSqlQuerySegmentLevel2,
      };
    };

    const queryGroups = backends.flatMap((backend) => {
      const [financialMetricsTableMetrics, otherMetrics] = partition(
        selectedmetricDetails.filter((m) =>
          backend.isMetricOfType(metricStore, m)
        ),
        // Same split as PbsTableService: only regular metrics read the
        // FINANCIALMETRICS table directly
        (m) =>
          backend.metricType === MetricTypes.REGULAR &&
          metricStore.isMetricFinancial(m)
      );
      return [
        {
          backend,
          metrics: financialMetricsTableMetrics,
          readsFinancialMetricsTable: true,
        },
        { backend, metrics: otherMetrics, readsFinancialMetricsTable: false },
      ].flatMap(({ backend, metrics, readsFinancialMetricsTable }) => {
        const queryInputs = getFiltersAndSegmentsForMetricKind(
          { metricType: backend.metricType, readsFinancialMetricsTable },
          { filters, segmentationLevel1, segmentationLevel2 }
        );
        // Skip metrics that can't be segmented by the selected dimension
        return metrics.length > 0 && queryInputs.segmentationLevel1.length > 0
          ? [{ backend, metrics, queryInputs }]
          : [];
      });
    });
    const results = await Promise.all(
      queryGroups.map(({ backend, metrics, queryInputs }) =>
        runSegmentationQueries(backend, metrics, queryInputs)
      )
    );
    const mergeResultsFor = (key: keyof (typeof results)[number]) =>
      mergeSegmentedResults(
        metricId,
        results.map((r) => r[key])
      );
    const res1 = mergeResultsFor('res1');
    const resOthers1 = mergeResultsFor('resOthers1');
    const res2 = mergeResultsFor('res2');
    const resOthers2 = mergeResultsFor('resOthers2');
    const finalResponseData: Record<string, ValueByDate> = {};
    const finalMeta: SegmentedResponseDataMetaData = { [metricId]: {} };
    const dateFormatter = formatPeriodConfigDate(
      getGranularityConfig(timeSliderConfig)
    );

    const populateResponseData = (res: SegmentedResponseData) => {
      Object.entries(res[metricId]).forEach(([segment, segmentVal]) => {
        finalResponseData[segment] = {};
        const timeSlices = getTimeSlicesAccToTimeSliderConfig(timeSliderConfig);
        timeSlices.forEach(({ dateRange }) => {
          const date = dateFormatter(dateRange[1]);
          finalResponseData[segment][date] = {};
          metricStore
            .getAllowedSortedMetricIdsForMetricGroup(metricId)
            .map((dimension) => {
              finalResponseData[segment][date][dimension.id] =
                segmentVal?.[date]?.[dimension.id] ?? null;
            });
        });
        finalResponseData[segment][PERIOD_TOTAL] = {};
        metricStore
          .getAllowedSortedMetricIdsForMetricGroup(metricId)
          .forEach((dimension) => {
            finalResponseData[segment][PERIOD_TOTAL][dimension.id] =
              segmentVal?.[PERIOD_TOTAL]?.[dimension.id] ?? null;
            if (selectedBenchmark) {
              finalResponseData[segment][selectedBenchmark.value] =
                finalResponseData[segment][selectedBenchmark.value] ?? {};
              finalResponseData[segment][selectedBenchmark.value][
                dimension.id
              ] = segmentVal?.[selectedBenchmark.value]?.[dimension.id] ?? null;
            }
          });
      });
    };

    const populateMetaData = (res: SegmentedResponseDataMetaData) => {
      finalMeta[metricId] = { ...finalMeta[metricId], ...res[metricId] };
    };

    const othersAllZero = allValuesEqual(resOthers1.responseData, [0, null]);

    [res1, res2, ...(othersAllZero ? [] : [resOthers1, resOthers2])].forEach(
      (res) => {
        populateResponseData(res.responseData);
        populateMetaData(res.meta);
      }
    );
    const timeSlices = getTimeSlicesAccToTimeSliderConfig(timeSliderConfig);

    const responseDataForAllSegments = Object.entries(finalResponseData).map(
      ([segmentValue, valueByDate]) => {
        const formattedResponse = getPbsServiceResponseData(
          valueByDate as ValueByDate,
          timeSlices,
          dateFormatter,
          selectedBenchmark
        );
        return { [segmentValue]: formattedResponse };
      },
      {} as Record<string, PbsServiceResponseData>
    );

    const responseDataGroupedBySeg1 = Object.values(
      groupBy(responseDataForAllSegments, (r) => {
        const [segmentKey, value] = Object.entries(r)[0];
        return segmentKey.split($SEGMENT_SEPARATOR$)[0];
      })
    );
    const SqlServiceResponseDataMonoid = new PbsServiceResponseDataMonoid();

    const formattedResponseData: Record<string, PbsServiceResponseData>[] =
      responseDataGroupedBySeg1.map((seg1Group) => {
        const [[onlySeg1Result], seg1Seg2Results] = partition(
          seg1Group,
          (r) => {
            return !Object.keys(r)[0].includes($SEGMENT_SEPARATOR$);
          }
        );
        const segments = seg1Seg2Results.map((result) => {
          const [segmentKey, valueSeg2] = Object.entries(result)[0];
          const [seg1, seg2] = segmentKey.split($SEGMENT_SEPARATOR$);
          return { [seg2]: valueSeg2 };
        });
        const [seg1Key, seg1Result] = Object.entries(onlySeg1Result)[0];
        return {
          [seg1Key]: {
            ...seg1Result,
            ...(segments.length
              ? {
                  segments: {
                    segments,
                    totalData: {
                      timeHeadersData: seg1Result.timeHeadersData,
                      fixedHeadersData: seg1Result.fixedHeadersData,
                      benchmarkHeadersData: seg1Result.benchmarkHeadersData,
                    },
                  },
                }
              : {}),
          },
        };
      });

    const metaDataGroupedBySeg1 = Object.values(
      groupBy(Object.entries(finalMeta[metricId]), ([segKey, segValue]) => {
        return segKey.split($SEGMENT_SEPARATOR$)[0];
      })
    );
    const formattedMetaData = Object.fromEntries(
      metaDataGroupedBySeg1.map((metaDataGroup) => {
        const [[onlySeg1Result], seg1Seg2Results] = partition(
          metaDataGroup,
          ([segKey]) => {
            return !segKey.includes($SEGMENT_SEPARATOR$);
          }
        );
        const [onlySeg1Key, onlySeg1Value] = onlySeg1Result;
        const combinedSeg1Value = { ...onlySeg1Value };
        combinedSeg1Value.segments = Object.fromEntries(
          seg1Seg2Results.map(([segmentKey, segmentData]) => {
            const [seg1, seg2] = segmentKey.split($SEGMENT_SEPARATOR$);
            return [seg2, segmentData];
          })
        );
        return [onlySeg1Key, combinedSeg1Value];
      })
    );
    const totalData: PbsServiceResponseData = formattedResponseData
      .flatMap((segmentData) => Object.values(segmentData))
      .reduce(
        (acc, PbsServiceResponseData) =>
          SqlServiceResponseDataMonoid.combine(acc, PbsServiceResponseData),
        SqlServiceResponseDataMonoid.empty
      );

    const { fixedHeaderObjs, timeHeaderObjs, benchmarkHeaderObjs } =
      getHeaderObjs(timeSliderConfig, selectedBenchmark, timeSliderStore);

    return {
      fixedHeaderObjs,
      timeHeaderObjs,
      benchmarkHeaderObjs,
      responseData: {
        [metricId]: formattedResponseData,
      },
      totalData,
      meta: { [metricId]: formattedMetaData },
    } as PbsSegmentationServiceResponse;
  };

  PbsSegmentationServiceForBackendMetrics.cacheKey = uuid();
  return getReactQueryWrappedService(PbsSegmentationServiceForBackendMetrics);
};
